import { useId, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/shared/cn';
import { vizToneColor, type VizTone } from './viz-tone';

export type TrendSeries = {
  name: string;
  points: readonly number[];
  tone: VizTone;
  /** An explicit stroke (a token such as `var(--ink-primary)`) for a series
   *  that carries no state, drawn instead of its tone colour. Opt-in; absent,
   *  the series draws in its tone as before. */
  color?: string;
  area?: boolean;
  /** Secondary-axis series scale independently when `rightYDomain` is set. */
  axis?: 'left' | 'right';
};

export type TrendReference = {
  value: number;
  label: string;
  tone: VizTone;
  /** An explicit line colour for a neutral reference (a design basis, not a
   *  limit), drawn instead of its tone colour. Opt-in; absent, as before. */
  color?: string;
  axis?: 'left' | 'right';
};

export type TrendMarker = {
  index: number;
  value: number;
  label: string;
  tone: VizTone;
  axis?: 'left' | 'right';
};

export type TrendChartKind = 'line' | 'bar';

/** A run of missing samples the series did not record, located in the chart's
 *  own category indices: `startIndex` is the last sample before the gap and
 *  `endIndex` the first after it, so the drawn band spans exactly the samples
 *  the line breaks between. Opt-in — when `gaps` is absent every chart renders
 *  exactly as before. */
export type TrendGap = {
  /** Index of the last sample before the gap. */
  startIndex: number;
  /** Index of the first sample after the gap. */
  endIndex: number;
  /** ISO stamps of those two samples, carried onto the drawn band. */
  startAt?: string;
  endAt?: string;
};

export type TrendChartProps = {
  series: readonly TrendSeries[];
  categories: readonly string[];
  references?: readonly TrendReference[];
  markers?: readonly TrendMarker[];
  kind?: TrendChartKind;
  height?: number;
  yDomain?: readonly [number, number];
  leftYDomain?: readonly [number, number];
  rightYDomain?: readonly [number, number];
  /** Explicit left-axis ticks on the caller's own round grid (the Control
   *  metallurgist landing's `10.2 / 10.5 / 10.8 / 11.1` pH, the locked comp's
   *  grid, whose 0.3 step no `niceStep` decade draws). When given, the axis
   *  draws exactly these, top-down, and each label prints the decimals its own
   *  grid needs — instead of the derived step. Pass with `leftYDomain` so the
   *  plot covers them. Opt-in; absent, every existing auto axis is unchanged. */
  leftYTicks?: readonly number[];
  /** Explicit right-axis ticks, as `leftYTicks`. */
  rightYTicks?: readonly number[];
  labelLastPoint?: boolean;
  /** Restrict the last-point label to one series by name (the Quality arsenic
   *  trend labels Pb/Ag only, so the Zn figure and its limit label stay clear
   *  of each other). With `labelLastPoint` alone every series is labelled.
   *  Ignored unless `labelLastPoint` is set. Default none. */
  labelLastPointSeries?: string;
  markerTitleFormatter?: (marker: TrendMarker, category: string) => string;
  className?: string;
  /** When two series share a tone, draw each later one in a shifted shade of
   * that tone as a dotted line with filled markers (legend swatch dotted too),
   * so the pair separates in both themes. Opt-in; default draws every series
   * as a solid line in its tone colour. */
  distinctSharedTones?: boolean;
  /** Pull reference-line labels this many px in from the plot's right edge so
   * they clear the last data point's marker. Default 0 (label ends at the edge). */
  referenceLabelInset?: number;
  /** `clear` places each reference label where no series line, marker or
   * earlier label runs: above or below its line, at the right end first, then
   * the left end, then inward. Falls back to the edge spot when nothing is
   * clear. Default `edge` (above the line at the right end). */
  referenceLabelPlacement?: 'edge' | 'clear';
  /** Draw each reference label in its reference's own tone (the crimson
   *  Pb/Ag limit label beside its crimson dashed line) instead of a flat
   *  ink-tertiary. The Quality arsenic trend's comp draws it this way.
   *  Opt-in; default keeps every existing chart's ink-tertiary labels. */
  referenceLabelTone?: boolean;
  /** A swatch-less note appended after the legend entries (`daily average`,
   *  the Quality arsenic comp's legend). Renders nothing when the chart has
   *  no legend. Default none. */
  legendNote?: string;
  /** Bar fill. `target` (default) tones each bar against the first
   *  reference, ok at or above it and warn below; `series` fills every bar in
   *  its series tone, for bars that carry no state (Cost's neutral daily
   *  margin, polish.P7). */
  barFill?: 'target' | 'series';
  /** Group tick labels by thousands (`15,000`). Default off. */
  groupThousands?: boolean;
  /** Unit appended to each left-axis tick label (`%` → `88%`). Default none. */
  yTickSuffix?: string;
  /** Axis title set under the category ticks (`Feed rate · stph`). Adds a row
   * of bottom padding only when given. Default none. */
  xAxisLabel?: string;
  /** Drop a stride tick label that would collide with the always-drawn first
   * or last label (irregular categories such as `74` beside `75.4`, or hourly
   * ticks in a narrow card). Default off. */
  avoidTickOverlap?: boolean;
  /** Draw a marker on every data point while the x slot allows it. Default
   *  true; turn off for a running total whose steps are the read. */
  pointMarkers?: boolean;
  /** Draw each marker's dashed vertical guide. Default true; turn off when the
   *  mark sits on a step the series already draws. */
  markerGuides?: boolean;
  /** Pixels between a marker's point and its label baseline. Default 7. Ignored
   *  when `markerLabelPlacement` is `clear`. */
  markerLabelOffset?: number;
  /** Width of the surface-coloured halo behind each marker label. Default 3;
   *  widen it where the series runs through the label, so the line breaks
   *  around the text instead of showing between its glyphs. */
  markerLabelHalo?: number;
  /** How a marker label is placed. `offset` (default) reproduces the historic
   *  spot, `markerLabelOffset` px above the mark. `clear` lifts the baseline
   *  above the series' highest value across the label's own horizontal extent
   *  (plus a small gap), drops it below the lowest value only when there is no
   *  room above, keeps every box inside the plot, and never lets two labels
   *  overlap. Opt-in, so every other chart is unchanged. */
  markerLabelPlacement?: 'offset' | 'clear';
  /** Drop a stride tick whose category repeats the always-drawn last label
   *  (an hourly grid whose last day already carries the final tick). Default
   *  off. */
  dedupeTickLabels?: boolean;
  /** Runs of missing samples (a trip the historian never recorded). When set,
   *  the line breaks across each gap and the gap is drawn as a light grey band,
   *  as the bearing case page's handoff trend draws the WO-441 trip. Callers
   *  derive the runs with the shared `sampleGaps` and map them to the chart's
   *  categories. Default none — every existing chart is unchanged. */
  gaps?: readonly TrendGap[];
  /** At most this many category ticks, evenly strided, first and last kept
   *  (a sparkline card's `06:00 · 09:00 · 11:30`). Default: a stride of one
   *  seventh of the categories. */
  tickCount?: number;
  /** Draw each reference line's label. Default true; turn off when the card
   *  states the reference in its own caption, so the series never strikes it. */
  referenceLabels?: boolean;
  /** How a marker is drawn. `ring` (default) draws the ring on the point with
   *  its label beside it (and the guide when `markerGuides`). `badge` draws the
   *  label as a numeral inside a small neutral disc set just below the point,
   *  no ring and no guide — the numbered steps of a sequence listed beside the
   *  chart (the Senior metallurgist's cascade, polish.P27). Opt-in, so every
   *  other chart is unchanged. */
  markerShape?: 'ring' | 'badge';
  /** Shaded horizontal bands behind the series (a Control Plan band 10.8–11.2
   *  under the Operator's Zn pH trace, polish.P16). Opt-in; default none, so
   *  every other chart is unchanged. */
  bands?: readonly { lo: number; hi: number }[];
  /** A state tone for one point's marker (a reading outside its band), drawn
   *  filled in that tone. Opt-in; absent, every marker draws as before. */
  pointTone?: (value: number, index: number) => VizTone | undefined;
};

/** `badge` markers: disc radius and its centre's offset below the point. */
const MARKER_BADGE_R = 7;
const MARKER_BADGE_DY = 16;

/** Extra bottom padding that holds an `xAxisLabel` row. */
const X_AXIS_LABEL_ROW = 14;

const PAD_FULL = { top: 12, right: 8, bottom: 28, left: 36 };
// Sparkline-height charts (< 96px) drop the y axis: labels would collide.
const PAD_COMPACT = { top: 6, right: 8, bottom: 18, left: 8 };
export type Pad = typeof PAD_FULL;
/** Below this per-point x slot the series draws as a bare line, no markers. */
const MARKER_MIN_SLOT_PX = 8;
export const padFor = (height: number): Pad => (height < 96 ? PAD_COMPACT : PAD_FULL);

export function chartGeometry(
  width: number,
  height: number,
  points: readonly number[],
  refValues: readonly number[],
  zeroBaseline = false,
  pad: Pad = PAD_FULL,
  yDomain?: readonly [number, number],
) {
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const yMin = yDomain ? yDomain[0] : zeroBaseline ? 0 : Math.min(...points, ...refValues);
  const yMax = yDomain ? yDomain[1] : Math.max(...points, ...refValues);
  const span = yMax - yMin || 1;
  const tickStep = niceStep(span / 4);
  const paddedMin = yDomain ? yDomain[0] : zeroBaseline ? 0 : Math.floor((yMin - span * 0.05) / tickStep) * tickStep;
  const paddedMax = yDomain ? yDomain[1] : Math.ceil((yMax + span * 0.05) / tickStep) * tickStep;
  const yRange = paddedMax - paddedMin || 1;

  const toX = (index: number, count: number) =>
    pad.left + (count <= 1 ? innerW / 2 : (index / (count - 1)) * innerW);
  const toY = (value: number) =>
    pad.top + innerH - ((value - paddedMin) / yRange) * innerH;

  return { toX, toY, paddedMin, paddedMax, tickStep, innerW, innerH };
}

function formatTick(value: number, step: number, groupThousands = false): string {
  // Decimals follow the step: 25 → "25", 2.5 → "2.5", 0.05 → "0.05".
  const decimals = Math.max(0, Math.ceil(-Math.log10(step)) + (step / 10 ** Math.floor(Math.log10(step)) % 1 ? 1 : 0));
  const fixed = Math.min(decimals, 3);
  if (!groupThousands) return value.toFixed(fixed);
  return value.toLocaleString('en-US', { minimumFractionDigits: fixed, maximumFractionDigits: fixed });
}

/** Stroke per series. With `distinct`, a series whose tone an earlier series
 * already drew takes a deeper shade of the same tone (still a state token). */
function seriesStrokes(series: readonly TrendSeries[], distinct: boolean): { stroke: string; shaded: boolean }[] {
  const seen = new Set<VizTone>();
  return series.map((row) => {
    const base = row.color ?? vizToneColor(row.tone);
    const shaded = distinct && seen.has(row.tone);
    seen.add(row.tone);
    return {
      stroke: shaded ? `color-mix(in srgb, ${base} 58%, var(--ink-primary))` : base,
      shaded,
    };
  });
}

function niceStep(rough: number): number {
  if (!Number.isFinite(rough) || rough <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const residual = rough / magnitude;
  const nice = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 2.5 ? 2.5 : residual <= 5 ? 5 : 10;
  return nice * magnitude;
}

/** The decimals an explicit tick grid prints: enough for every tick, never
 *  more (`10.2 / 10.5 / 10.8 / 11.1` → 1, `72 / 76 / 80 / 84` → 0). Fed back
 *  to `formatTick` as `10 ** -decimals`, so a grid whose step is off the
 *  `niceStep` decades still prints its own precision. */
function tickDecimals(ticks: readonly number[]): number {
  let decimals = 0;
  for (const tick of ticks) {
    for (let d = 0; d <= 3; d += 1) {
      if (Math.abs(Number(tick.toFixed(d)) - tick) < 1e-9) {
        decimals = Math.max(decimals, d);
        break;
      }
    }
  }
  return decimals;
}

/** Marker-label metrics for the 10px label: per-character advance, cap height
 *  above the baseline, and descent below it. `CAP_H + DESCENT` matches the
 *  measured 13px ink box of the drawn label. */
const MARKER_LABEL_CHAR_W = 6;
const MARKER_LABEL_CAP_H = 10;
const MARKER_LABEL_DESCENT = 3;
export const MARKER_LABEL_LINE_H = MARKER_LABEL_CAP_H + MARKER_LABEL_DESCENT;
/** Clearance between a label's box and the series (or an earlier label), px. */
export const MARKER_LABEL_GAP = 6;
/** Horizontal offset of a label's near edge from its mark, px. */
const MARKER_LABEL_EDGE = 6;
/** Marker ring geometry: the circle's radius plus half its stroke, so the
 *  clearance test and a domain that reserves headroom count the ring's drawn
 *  extent rather than its centre line. */
const MARKER_RING_R = 4;
const MARKER_RING_STROKE = 2;
export const MARKER_RING_OUTER_R = MARKER_RING_R + MARKER_RING_STROKE / 2;

/** How much of the plot's inner height a `clear` label needs above the curve's
 *  maximum: its own 13px ink box, the 6px gap under it, and the 5px outer
 *  radius of the ring it sits over. Exported so a chart that sizes its own
 *  domain for `clear` labels can reserve exactly the placement's own height —
 *  without it the highest mark's label has nowhere to go but below the curve. */
export function markerLabelHeadroomFraction(height: number): number {
  const pad = padFor(height);
  return (MARKER_LABEL_LINE_H + MARKER_LABEL_GAP + MARKER_RING_OUTER_R) / (height - pad.top - pad.bottom);
}

export type MarkerLabelBox = { l: number; t: number; r: number; b: number };

export type MarkerLabelPlacement = {
  /** Text x — the box's left edge for `start`, its right edge for `end`. */
  x: number;
  /** Text baseline. */
  y: number;
  anchor: 'start' | 'end';
  /** The glyph box in SVG user units; `t` is the cap top, `b` the descent. */
  box: MarkerLabelBox;
};

/** `clear`-mode marker-label geometry. Each label's box is lifted above the
 *  series' highest value across the index range the box's x extent covers
 *  (plus a gap) — and above every marker ring that extent reaches, because a
 *  ring stands `MARKER_RING_OUTER_R` above its line and the historic spot
 *  grazed its top. When that would leave the plot it drops below the series'
 *  lowest value over the same range. Both sides of a mark are tried and the
 *  first clear box nearest the mark's own ring wins, so a wide label sits
 *  beside its own step instead of being lifted over a later mark's ring; the
 *  preferred side wins a near-tie. Every box is clamped inside the plot, so no
 *  box meets an x-axis tick, and a box that would meet an earlier one is
 *  rejected. Returns one entry per marker (null when the marker is off the
 *  series), so the caller can keep its index alignment. Pure — the probe calls
 *  it with the same geometry the chart draws. */
function placeMarkerLabels(args: {
  markers: readonly TrendMarker[];
  points: readonly number[];
  categoryCount: number;
  width: number;
  pad: Pad;
  toX: (i: number, n: number) => number;
  toY: (v: number) => number;
  baselineY: number;
}): (MarkerLabelPlacement | null)[] {
  const { markers, points, categoryCount, width, pad, toX, toY, baselineY } = args;
  const plotLeft = pad.left;
  const plotRight = width - pad.right;
  const innerW = Math.max(1, plotRight - plotLeft);
  const lastIndex = points.length - 1;
  const placed: MarkerLabelBox[] = [];
  const overlaps = (a: MarkerLabelBox, b: MarkerLabelBox) =>
    a.l < b.r && a.r > b.l && a.t < b.b && a.b > b.t;
  const ringBox = (cx: number, cy: number): MarkerLabelBox => ({
    l: cx - MARKER_RING_OUTER_R,
    t: cy - MARKER_RING_OUTER_R,
    r: cx + MARKER_RING_OUTER_R,
    b: cy + MARKER_RING_OUTER_R,
  });
  /** Every drawn ring's bounding box, the obstacle the label must clear. */
  const rings = markers
    .filter((marker) => marker.index >= 0 && marker.index <= lastIndex && Number.isFinite(points[marker.index]))
    .map((marker) => ringBox(toX(marker.index, categoryCount), toY(marker.value)));
  /** Distance between two boxes; 0 when they touch or cross. */
  const gapBetween = (a: MarkerLabelBox, b: MarkerLabelBox): number => {
    const dx = Math.max(0, Math.max(b.l - a.r, a.l - b.r));
    const dy = Math.max(0, Math.max(b.t - a.b, a.t - b.b));
    return Math.hypot(dx, dy);
  };

  return markers.map((marker) => {
    if (points.length === 0 || marker.index < 0 || marker.index > lastIndex) return null;
    const x = toX(marker.index, categoryCount);
    const w = marker.label.length * MARKER_LABEL_CHAR_W;
    const preferred: 'start' | 'end' = marker.index > categoryCount * 0.7 ? 'end' : 'start';
    const ownRing = ringBox(x, toY(marker.value));
    let best: MarkerLabelPlacement | null = null;
    let bestScore = Infinity;
    let first: MarkerLabelPlacement | null = null;
    for (const anchor of [preferred, preferred === 'start' ? 'end' : 'start'] as const) {
      let l = anchor === 'start' ? x + MARKER_LABEL_EDGE : x - MARKER_LABEL_EDGE - w;
      let r = l + w;
      // Horizontal clamp; a label longer than the plot still sits inside.
      if (r > plotRight) { const d = r - plotRight; l -= d; r -= d; }
      if (l < plotLeft) { const d = plotLeft - l; l += d; r += d; }
      // The index range the box covers, with a point either side so a steep
      // segment that crosses the box edge is accounted for. The marker's own
      // value is folded in: an end-anchored label sits beside its own ring, so
      // the curve it must clear includes that ring's step, not only the span.
      const indexAt = (px: number) => ((px - pad.left) / innerW) * lastIndex;
      const i0 = Math.max(0, Math.floor(indexAt(l)));
      const i1 = Math.min(lastIndex, Math.ceil(indexAt(r)));
      let maxV = points[marker.index]!;
      let minV = points[marker.index]!;
      for (let i = i0; i <= i1; i += 1) {
        const v = points[i]!;
        if (v > maxV) maxV = v;
        if (v < minV) minV = v;
      }
      if (!Number.isFinite(maxV)) { maxV = 0; minV = 0; }
      // Above the extent's highest sample, and above every ring the box's x
      // extent reaches (a ring's outer edge stands 5px above its line, where
      // the line-only spot grazed the label's descender).
      let top = toY(maxV) - MARKER_LABEL_GAP - MARKER_LABEL_CAP_H;
      for (const ring of rings) {
        if (ring.r < l || ring.l > r) continue;
        top = Math.min(top, ring.t - MARKER_LABEL_GAP - MARKER_LABEL_CAP_H);
      }
      let below = false;
      if (top < pad.top) {
        const under = toY(minV) + MARKER_LABEL_GAP;
        if (under + MARKER_LABEL_LINE_H <= baselineY) { top = under; below = true; }
      }
      top = Math.min(Math.max(top, pad.top), baselineY - MARKER_LABEL_LINE_H);
      const box: MarkerLabelBox = { l, t: top, r, b: top + MARKER_LABEL_LINE_H };
      const placement: MarkerLabelPlacement = {
        x: anchor === 'start' ? box.l : box.r,
        y: box.t + MARKER_LABEL_CAP_H,
        anchor,
        box,
      };
      if (!first) first = placement;
      if (rings.some((ring) => overlaps(box, ring))) continue;
      if (placed.some((prev) => overlaps(box, prev))) continue;
      // Nearest its own ring wins; a below-curve box and the far side of the
      // mark are the fallbacks, so a near-tie keeps the preferred side.
      const score = gapBetween(box, ownRing) + (anchor === preferred ? 0 : 1) + (below ? 2 : 0);
      if (score < bestScore) { bestScore = score; best = placement; }
    }
    // Every candidate met a ring or an earlier label: keep the first spot and
    // nudge clear of the placed labels so the label still draws.
    if (!best && first) {
      let box = first.box;
      for (const prev of placed) {
        if (!overlaps(box, prev)) continue;
        const up = prev.t - MARKER_LABEL_GAP - MARKER_LABEL_LINE_H;
        const down = prev.b + MARKER_LABEL_GAP;
        if (up >= pad.top) box = { ...box, t: up, b: up + MARKER_LABEL_LINE_H };
        else if (down + MARKER_LABEL_LINE_H <= baselineY) box = { ...box, t: down, b: down + MARKER_LABEL_LINE_H };
      }
      best = { ...first, y: box.t + MARKER_LABEL_CAP_H, box };
    }
    if (!best) return null;
    placed.push(best.box);
    return best;
  });
}

/** The `clear`-mode label layout for a line chart's left axis, from the same
 *  geometry the chart draws. Exported so the acceptance probe measures the
 *  label boxes it renders. */
export function markerLabelLayout(args: {
  points: readonly number[];
  markers: readonly TrendMarker[];
  categoryCount: number;
  width: number;
  height: number;
  yDomain?: readonly [number, number];
}): (MarkerLabelPlacement | null)[] {
  const pad = padFor(args.height);
  const geom = chartGeometry(args.width, args.height, args.points, [], false, pad, args.yDomain);
  return placeMarkerLabels({
    markers: args.markers,
    points: args.points,
    categoryCount: args.categoryCount,
    width: args.width,
    pad,
    toX: geom.toX,
    toY: geom.toY,
    baselineY: pad.top + geom.innerH,
  });
}

function polyline(
  points: readonly number[],
  toX: (i: number, n: number) => number,
  toY: (v: number) => number,
): string {
  return points
    .map((value, index) => {
      const x = toX(index, points.length);
      const y = toY(value);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
}

function areaPath(
  points: readonly number[],
  toX: (i: number, n: number) => number,
  toY: (v: number) => number,
  baselineY: number,
): string {
  if (points.length === 0) return '';
  const line = polyline(points, toX, toY).replace(/^M/, '');
  const lastX = toX(points.length - 1, points.length);
  const firstX = toX(0, points.length);
  return `M ${firstX} ${baselineY} L ${line} L ${lastX} ${baselineY} Z`;
}

/** The index ranges a series draws as one stroke when `gaps` are given: the
 *  run is split at each gap, whose own observed samples are the boundary pair
 *  (`startIndex` ends the segment before, `endIndex` begins the one after).
 *  Gaps outside the series or with no span are ignored. */
function lineSegments(count: number, gaps: readonly TrendGap[]): [number, number][] {
  const last = count - 1;
  const breaks = gaps
    .filter((gap) => gap.startIndex >= 0 && gap.endIndex > gap.startIndex)
    .map((gap) => ({ from: Math.min(gap.startIndex, last), to: Math.min(gap.endIndex, last) }))
    .sort((a, b) => a.from - b.from);
  const segments: [number, number][] = [];
  let start = 0;
  for (const boundary of breaks) {
    if (boundary.from >= start) segments.push([start, boundary.from]);
    start = Math.max(start, boundary.to);
  }
  if (start <= last) segments.push([start, last]);
  return segments.filter(([from, to]) => to >= from);
}

/** `polyline` over one index range, keeping the full series' x positions so a
 *  broken line's segments still land on their own categories. */
function polylineRange(
  points: readonly number[],
  from: number,
  to: number,
  toX: (i: number, n: number) => number,
  toY: (v: number) => number,
): string {
  let d = '';
  for (let index = from; index <= to; index += 1) {
    d += `${index === from ? 'M' : 'L'} ${toX(index, points.length)} ${toY(points[index]!)}`;
  }
  return d;
}

function polylineSegments(
  points: readonly number[],
  toX: (i: number, n: number) => number,
  toY: (v: number) => number,
  segments: readonly [number, number][],
): string {
  return segments.map(([from, to]) => polylineRange(points, from, to, toX, toY)).join(' ');
}

function areaSegments(
  points: readonly number[],
  toX: (i: number, n: number) => number,
  toY: (v: number) => number,
  baselineY: number,
  segments: readonly [number, number][],
): string {
  return segments
    .map(([from, to]) => {
      const line = polylineRange(points, from, to, toX, toY).replace(/^M/, '');
      return `M ${toX(from, points.length)} ${baselineY} L ${line} L ${toX(to, points.length)} ${baselineY} Z`;
    })
    .join(' ');
}

/** Dotted stroke for a series drawn in a shared tone: round dots, clearly
 * not the thin 4-3 dash of a reference line. */
const SHADED_DASH = '0.1 5';

type RefLabelPos = { x: number; y: number; anchor: 'start' | 'middle' | 'end' };

/** Where each reference label sits. `edge` reproduces the historic spot
 * (above the line, ending at the plot's right edge less `inset`). `clear`
 * tries spots above / below the line at the right end, the left end, then
 * inward, and takes the first whose box no series line, marker or earlier
 * label enters. */
function referenceLabelPositions(args: {
  references: readonly TrendReference[];
  series: readonly TrendSeries[];
  categories: number;
  width: number;
  pad: Pad;
  dualAxis: boolean;
  toX: (i: number, n: number) => number;
  refY: (ref: TrendReference) => number;
  seriesY: (row: TrendSeries) => (v: number) => number;
  inset: number;
  placement: 'edge' | 'clear';
}): RefLabelPos[] {
  const { references, series, width, pad, dualAxis, toX, refY, seriesY, inset, placement } = args;
  const edgeX = (ref: TrendReference) =>
    (ref.axis === 'right' && dualAxis ? width - pad.right - 6 : width - pad.right) - inset;
  if (placement === 'edge') {
    return references.map((ref) => ({ x: edgeX(ref), y: refY(ref) - 4, anchor: 'end' as const }));
  }
  const paths = series.map((row) => {
    const toY = seriesY(row);
    return row.points.map((v, i) => [toX(i, row.points.length), toY(v)] as const);
  });
  const lineYAt = (pts: readonly (readonly [number, number])[], x: number): number | null => {
    for (let i = 1; i < pts.length; i++) {
      const [x0, y0] = pts[i - 1]!;
      const [x1, y1] = pts[i]!;
      if (x >= Math.min(x0, x1) && x <= Math.max(x0, x1)) {
        return x1 === x0 ? y0 : y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
      }
    }
    return null;
  };
  type Box = { l: number; r: number; t: number; b: number };
  const placed: Box[] = [];
  const hits = (box: Box): boolean => {
    for (const pts of paths) {
      for (const [px, py] of pts) {
        if (px >= box.l - 5 && px <= box.r + 5 && py >= box.t - 5 && py <= box.b + 5) return true;
      }
      for (let x = box.l; x <= box.r; x += 2) {
        const y = lineYAt(pts, x);
        if (y !== null && y >= box.t - 2 && y <= box.b + 2) return true;
      }
    }
    return placed.some((p) => box.l < p.r && box.r > p.l && box.t < p.b && box.b > p.t);
  };
  const plotLeft = pad.left + 4;
  return references.map((ref) => {
    const y = refY(ref);
    const w = ref.label.length * 5.4 + 4;
    const right = edgeX(ref);
    const spots: RefLabelPos[] = [];
    const xs: { x: number; anchor: RefLabelPos['anchor'] }[] = [
      { x: right, anchor: 'end' },
      { x: plotLeft, anchor: 'start' },
      ...[0.75, 0.5, 0.25].map((f) => ({
        x: plotLeft + (right - plotLeft) * f,
        anchor: 'middle' as const,
      })),
    ];
    for (const { x, anchor } of xs) {
      spots.push({ x, y: y - 4, anchor }, { x, y: y + 12, anchor });
    }
    const boxOf = (s: RefLabelPos): Box => {
      const l = s.anchor === 'end' ? s.x - w : s.anchor === 'middle' ? s.x - w / 2 : s.x;
      return { l, r: l + w, t: s.y - 9, b: s.y + 2 };
    };
    const chosen = spots.find((s) => !hits(boxOf(s))) ?? spots[0]!;
    placed.push(boxOf(chosen));
    return chosen;
  });
}

function barToneForValue(value: number, target?: number): VizTone {
  if (target === undefined) return 'ok';
  return value >= target ? 'ok' : 'warn';
}

function chartAccessibleSummary(
  series: readonly TrendSeries[],
  categories: readonly string[],
  references: readonly TrendReference[],
  markers: readonly TrendMarker[],
  kind: TrendChartKind,
): { title: string; description: string } {
  const seriesParts = series.map((row) => {
    const pairs = row.points
      .map((point, index) => `${categories[index] ?? ''}: ${point}`)
      .join(', ');
    return `${row.name}: ${pairs}`;
  });
  const refParts = references.map((ref) => `${ref.label} at ${ref.value}`);
  const markerParts = markers.map((marker) =>
    `${marker.label} at ${categories[marker.index] ?? ''}: ${marker.value}`,
  );
  const title = kind === 'bar' ? 'Bar chart' : 'Trend chart';
  const description = [
    ...seriesParts,
    refParts.length ? `Reference lines: ${refParts.join('; ')}` : '',
    markerParts.length ? `Markers: ${markerParts.join('; ')}` : '',
  ]
    .filter(Boolean)
    .join('. ');
  return { title, description };
}

export function TrendChart({
  series,
  categories,
  references = [],
  markers = [],
  kind = 'line',
  height = 160,
  yDomain,
  leftYDomain,
  rightYDomain,
  leftYTicks,
  rightYTicks,
  labelLastPoint = false,
  labelLastPointSeries,
  markerTitleFormatter,
  className,
  distinctSharedTones = false,
  referenceLabelInset = 0,
  referenceLabelPlacement = 'edge',
  referenceLabelTone = false,
  legendNote,
  barFill = 'target',
  groupThousands = false,
  yTickSuffix = '',
  xAxisLabel,
  avoidTickOverlap = false,
  pointMarkers = true,
  markerGuides = true,
  markerLabelOffset = 7,
  markerLabelHalo = 3,
  markerLabelPlacement = 'offset',
  dedupeTickLabels = false,
  gaps = [],
  tickCount,
  referenceLabels = true,
  markerShape = 'ring',
  bands = [],
  pointTone,
}: TrendChartProps) {
  // SVG url(#id) needs a plain id — useId() carries colons and series names carry spaces/slashes.
  const gradientId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const titleId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const descId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const { title: chartTitle, description: chartDescription } = chartAccessibleSummary(
    series,
    categories,
    references,
    markers,
    kind,
  );
  // Render at the container's pixel width and the given pixel height: the SVG
  // never scales its type with the panel (a 640-wide viewBox stretched to a
  // 1130px panel turned 10px ticks into 17px and a 180px chart into 280px).
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(240, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const leftSeries = series.filter((row) => row.axis !== 'right');
  const rightSeries = series.filter((row) => row.axis === 'right');
  const leftRefs = references.filter((row) => row.axis !== 'right');
  const rightRefs = references.filter((row) => row.axis === 'right');
  const dualAxis = rightYDomain !== undefined && rightSeries.length > 0;
  const resolvedLeftDomain = leftYDomain ?? yDomain;
  const allLeftPoints = leftSeries.flatMap((row) => row.points);
  const leftRefValues = leftRefs.map((row) => row.value);
  const basePad = dualAxis ? { ...PAD_FULL, right: 48 } : padFor(height);
  const compact = basePad === PAD_COMPACT;
  // Grouped ticks (`15,000`) are wider than the 36 px default gutter.
  const groupedPad = groupThousands && !compact ? { ...basePad, left: Math.max(basePad.left, 48) } : basePad;
  const pad = xAxisLabel && !compact ? { ...groupedPad, bottom: groupedPad.bottom + X_AXIS_LABEL_ROW } : groupedPad;
  const tickRowY = height - 6 - (xAxisLabel && !compact ? X_AXIS_LABEL_ROW : 0);
  const leftGeom = chartGeometry(
    width,
    height,
    allLeftPoints,
    leftRefValues,
    kind === 'bar',
    pad,
    resolvedLeftDomain,
  );
  const rightGeom = dualAxis
    ? chartGeometry(
        width,
        height,
        rightSeries.flatMap((row) => row.points),
        rightRefs.map((row) => row.value),
        kind === 'bar',
        pad,
        rightYDomain,
      )
    : null;
  const { toX, toY, paddedMin, paddedMax, tickStep, innerH } = leftGeom;
  const toYRight = rightGeom?.toY ?? toY;
  const baselineY = pad.top + innerH;
  // Opt-in explicit grids. A compact chart (< 96px) still drops the y axis, so
  // an explicit grid is ignored there exactly as the derived one is.
  const explicitLeftTicks = leftYTicks?.length && !compact ? leftYTicks : null;
  const explicitRightTicks = rightYTicks?.length && !compact ? rightYTicks : null;
  const yTicks: number[] = explicitLeftTicks ? [...explicitLeftTicks].sort((a, b) => b - a) : [];
  if (!explicitLeftTicks) {
    for (let tick = paddedMax; !compact && tick >= paddedMin - tickStep / 2; tick -= tickStep) {
      yTicks.push(Number(tick.toFixed(6)));
    }
  }
  const leftTickStep = explicitLeftTicks ? 10 ** -tickDecimals(explicitLeftTicks) : tickStep;
  const rightTicks: number[] = explicitRightTicks
    ? [...explicitRightTicks].sort((a, b) => b - a)
    : [];
  if (rightGeom && !compact && !explicitRightTicks) {
    for (
      let tick = rightGeom.paddedMax;
      tick >= rightGeom.paddedMin - rightGeom.tickStep / 2;
      tick -= rightGeom.tickStep
    ) {
      rightTicks.push(Number(tick.toFixed(6)));
    }
  }
  const rightTickStep = explicitRightTicks
    ? 10 ** -tickDecimals(explicitRightTicks)
    : rightGeom?.tickStep ?? tickStep;
  const seriesToY = (row: TrendSeries) => (row.axis === 'right' && dualAxis ? toYRight : toY);
  const labelStride = tickCount && tickCount > 1
    ? Math.max(1, Math.ceil((categories.length - 1) / (tickCount - 1)))
    : Math.max(1, Math.ceil(categories.length / 7));
  const barSeries = kind === 'bar' ? series[0] : null;
  const barTarget = references[0]?.value;
  const barSlotW = barSeries
    ? (width - pad.left - pad.right) / Math.max(barSeries.points.length, 1)
    : 0;
  const legendSeries = kind === 'bar' && barSeries ? [barSeries] : series;
  const strokes = seriesStrokes(series, distinctSharedTones);
  const refLabels = referenceLabelPositions({
    references,
    series: kind === 'bar' ? [] : series,
    categories: categories.length,
    width,
    pad,
    dualAxis,
    toX,
    refY: (ref) => (ref.axis === 'right' && dualAxis ? toYRight(ref.value) : toY(ref.value)),
    seriesY: (row) => seriesToY(row),
    inset: referenceLabelInset,
    placement: referenceLabelPlacement,
  });
  const strokeFor = (row: TrendSeries) => strokes[series.indexOf(row)] ?? { stroke: row.color ?? vizToneColor(row.tone), shaded: false };
  // Opt-in `clear` label placement: one box per marker, lifted off the series
  // it annotates and clamped inside the plot. `offset` mode leaves them null
  // so the historic spot is drawn unchanged.
  const markerLabels =
    markerLabelPlacement === 'clear'
      ? placeMarkerLabels({ markers, points: allLeftPoints, categoryCount: categories.length, width, pad, toX, toY, baselineY })
      : null;
  const markerLabelsRight =
    markerLabelPlacement === 'clear' && dualAxis
      ? placeMarkerLabels({
          markers,
          points: rightSeries.flatMap((row) => row.points),
          categoryCount: categories.length,
          width,
          pad,
          toX,
          toY: toYRight,
          baselineY,
        })
      : null;
  const lastCategory = categories[categories.length - 1];

  return (
    <div ref={wrapperRef} className={cn('w-full', className)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block max-w-full"
        role="graphics-document"
        aria-labelledby={`${titleId} ${descId}`}
      >
        <title id={titleId}>{chartTitle}</title>
        <desc id={descId}>{chartDescription}</desc>
        {yTicks.map((tick) => {
          const y = toY(tick);
          return (
            <g key={tick}>
              <line
                x1={pad.left}
                x2={width - pad.right}
                y1={y}
                y2={y}
                stroke="var(--hairline)"
                strokeWidth={1}
              />
              <text
                x={pad.left - 6}
                y={y + 3}
                textAnchor="end"
                data-trend-y-tick="left"
                className="fill-[var(--ink-tertiary)] text-[10px] tabular-nums"
              >
                {formatTick(tick, leftTickStep, groupThousands)}{yTickSuffix}
              </text>
            </g>
          );
        })}

        {/* Opt-in outage bands: a light grey wash under the reference lines and
            the series, spanning exactly the samples the line breaks between. */}
        {kind === 'line'
          ? gaps.map((gap, index) => {
              const xStart = toX(gap.startIndex, categories.length);
              const xEnd = toX(gap.endIndex, categories.length);
              const left = Math.min(xStart, xEnd);
              return (
                <rect
                  key={`gap-${index}-${gap.startAt ?? gap.startIndex}`}
                  data-trend-gap
                  data-gap-start={gap.startAt}
                  data-gap-end={gap.endAt}
                  x={left}
                  y={pad.top}
                  width={Math.max(2, Math.abs(xEnd - xStart))}
                  height={innerH}
                  fill="var(--ink-tertiary)"
                  opacity={0.14}
                />
              );
            })
          : null}

        {/* Opt-in value bands (a Control Plan band): a hairline wash across the
            plot between the band's limits, under the references and series. */}
        {bands.map((band, index) => {
          const yTop = toY(Math.max(band.lo, band.hi));
          const yBottom = toY(Math.min(band.lo, band.hi));
          return (
            <rect
              key={`band-${index}`}
              data-trend-band
              x={pad.left}
              y={yTop}
              width={Math.max(0, width - pad.left - pad.right)}
              height={Math.max(0, yBottom - yTop)}
              fill="var(--hairline)"
            />
          );
        })}

        {references.map((ref, index) => {
          const y = ref.axis === 'right' && dualAxis ? toYRight(ref.value) : toY(ref.value);
          const { x: labelX, y: labelY, anchor } = refLabels[index]!;
          return (
            <g key={`${index}-${ref.label}`}>
              <line
                x1={pad.left}
                x2={width - pad.right}
                y1={y}
                y2={y}
                stroke={ref.color ?? vizToneColor(ref.tone)}
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              {referenceLabels ? (
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor={anchor}
                  fill={referenceLabelTone ? ref.color ?? vizToneColor(ref.tone) : undefined}
                  className={referenceLabelTone ? 'text-[10px]' : 'fill-[var(--ink-tertiary)] text-[10px]'}
                >
                  {ref.label}
                </text>
              ) : null}
            </g>
          );
        })}

        {kind === 'bar' && barSeries
          ? barSeries.points.map((point, index) => {
              const tone = barFill === 'series' ? barSeries.tone : barToneForValue(point, barTarget);
              const fill = vizToneColor(tone);
              const barW = Math.max(4, barSlotW * 0.62);
              const cx = pad.left + barSlotW * index + barSlotW / 2;
              const y = toY(point);
              const barH = baselineY - y;
              return (
                <rect
                  key={`${barSeries.name}-${index}`}
                  x={cx - barW / 2}
                  y={y}
                  width={barW}
                  height={Math.max(0, barH)}
                  rx={2}
                  fill={fill}
                >
                  <title>
                    {barSeries.name} · {categories[index] ?? ''}: {point}
                  </title>
                </rect>
              );
            })
          : series.map((row, idx) => {
              const { stroke, shaded } = strokeFor(row);
              const rowToY = seriesToY(row);
              const segments = gaps.length > 0 ? lineSegments(row.points.length, gaps) : null;
              const line = segments
                ? polylineSegments(row.points, toX, rowToY, segments)
                : polyline(row.points, toX, rowToY);
              const area = row.area
                ? segments
                  ? areaSegments(row.points, toX, rowToY, baselineY, segments)
                  : areaPath(row.points, toX, rowToY, baselineY)
                : null;
              return (
                <g key={row.name}>
                  {area ? (
                    <>
                      <defs>
                        <linearGradient
                          id={`${gradientId}-${idx}`}
                          x1="0"
                          x2="0"
                          y1="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor={stroke} stopOpacity={0.22} />
                          <stop offset="100%" stopColor={stroke} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <path d={area} fill={`url(#${gradientId}-${idx})`} />
                    </>
                  ) : null}
                  <path
                    d={line}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={shaded ? 2.5 : 2}
                    strokeLinejoin="round"
                    strokeLinecap={shaded ? 'round' : undefined}
                    strokeDasharray={shaded ? SHADED_DASH : undefined}
                  />
                  {/* Markers only while each point owns at least 8 px of x —
                      a dense series (an hourly week is 168 points) reads as
                      its line, and a marker per point would bury it. */}
                  {pointMarkers && row.points.length * MARKER_MIN_SLOT_PX <= width - pad.left - pad.right
                    ? row.points.map((point, index) => {
                      const toned = pointTone?.(point, index);
                      return (
                    <circle
                      key={`${row.name}-${index}`}
                      cx={toX(index, row.points.length)}
                      cy={rowToY(point)}
                      r={3}
                      fill={toned ? vizToneColor(toned) : shaded ? stroke : 'var(--surface-2)'}
                      stroke={toned ? vizToneColor(toned) : stroke}
                      strokeWidth={1.5}
                      data-trend-point-tone={toned}
                    >
                      <title>
                        {row.name} · {categories[index] ?? ''}: {point}
                      </title>
                    </circle>
                      );
                    })
                    : null}
                  {labelLastPoint && (labelLastPointSeries === undefined || row.name === labelLastPointSeries) && row.points.length > 0 ? (
                    <text
                      x={toX(row.points.length - 1, row.points.length)}
                      y={rowToY(row.points[row.points.length - 1]!) - 7}
                      textAnchor="end"
                      className="fill-[var(--ink-secondary)] text-[10px] tabular-nums"
                    >
                      {row.points[row.points.length - 1]}%
                    </text>
                  ) : null}
                </g>
              );
            })}

        {rightTicks.map((tick) => {
          const y = toYRight(tick);
          return (
            <text
              key={`r-${tick}`}
              x={width - pad.right + 8}
              y={y + 3}
              textAnchor="start"
              data-trend-y-tick="right"
              className="fill-[var(--ink-tertiary)] text-[10px] tabular-nums"
            >
              {formatTick(tick, rightTickStep, groupThousands)}
            </text>
          );
        })}

        {/* Reference labels paint after the series so a line that runs along
            its own limit never strikes the text; the halo lifts it off markers. */}
        {(referenceLabels ? references : []).map((ref, index) => (
          <text
            key={`label-${index}-${ref.label}`}
            x={refLabels[index]!.x}
            y={refLabels[index]!.y}
            textAnchor={refLabels[index]!.anchor}
            paintOrder="stroke"
            stroke="var(--surface-2)"
            strokeWidth={3}
            fill={referenceLabelTone ? ref.color ?? vizToneColor(ref.tone) : undefined}
            className={referenceLabelTone ? 'text-[10px]' : 'fill-[var(--ink-tertiary)] text-[10px]'}
          >
            {ref.label}
          </text>
        ))}

        {markers.map((marker, index) => {
          const x = toX(marker.index, categories.length);
          const y = marker.axis === 'right' && dualAxis ? toYRight(marker.value) : toY(marker.value);
          const placement = marker.axis === 'right' && dualAxis ? markerLabelsRight?.[index] ?? null : markerLabels?.[index] ?? null;
          const textAnchor = placement ? placement.anchor : marker.index > categories.length * 0.7 ? 'end' : 'start';
          const labelX = placement ? placement.x : marker.index > categories.length * 0.7 ? x - 6 : x + 6;
          const labelY = placement ? placement.y : y - markerLabelOffset;
          if (markerShape === 'badge') {
            const cy = Math.min(y + MARKER_BADGE_DY, baselineY - MARKER_BADGE_R - 1);
            return (
              <g key={`marker-${index}-${marker.label}`} data-trend-marker-badge={marker.label}>
                <circle cx={x} cy={cy} r={MARKER_BADGE_R} fill="var(--surface-2)" stroke="var(--hairline-strong)" strokeWidth={1}>
                  <title>{markerTitleFormatter
                    ? markerTitleFormatter(marker, categories[marker.index] ?? '')
                    : `${marker.label} · ${categories[marker.index] ?? ''}`}</title>
                </circle>
                <text
                  x={x}
                  y={cy + 3.5}
                  textAnchor="middle"
                  className="fill-[var(--ink-secondary)] text-[10px] font-semibold"
                >
                  {marker.label}
                </text>
              </g>
            );
          }
          return (
            <g key={`marker-${index}-${marker.label}`}>
              {markerGuides ? (
                <line
                  x1={x}
                  x2={x}
                  y1={pad.top}
                  y2={baselineY}
                  stroke={vizToneColor(marker.tone)}
                  strokeWidth={1}
                  strokeDasharray="2 3"
                />
              ) : null}
              <circle
                cx={x}
                cy={y}
                r={MARKER_RING_R}
                fill="var(--surface-2)"
                stroke={vizToneColor(marker.tone)}
                strokeWidth={MARKER_RING_STROKE}
              >
                <title>{markerTitleFormatter
                  ? markerTitleFormatter(marker, categories[marker.index] ?? '')
                  : `${marker.label} · ${categories[marker.index] ?? ''} stph: ${marker.value}%`}</title>
              </circle>
              <text
                x={labelX}
                y={labelY}
                textAnchor={textAnchor}
                paintOrder="stroke"
                stroke="var(--surface-2)"
                strokeWidth={markerLabelHalo}
                strokeLinejoin={placement ? 'round' : undefined}
                className="fill-[var(--ink-secondary)] text-[10px] font-medium"
              >
                {marker.label}
              </text>
            </g>
          );
        })}

        {categories.map((label, index) => {
          if (index % labelStride !== 0 && index !== categories.length - 1) return null;
          if (dedupeTickLabels && index !== categories.length - 1 && label === lastCategory) return null;
          if (avoidTickOverlap && index !== categories.length - 1 && index !== 0) {
            const lastIndex = categories.length - 1;
            const gap = toX(lastIndex, categories.length) - toX(index, categories.length);
            // Centred label vs end-anchored last label, 10px ticks ≈ 6px per character.
            const needed = (label.length * 6) / 2 + (categories[lastIndex]!.length * 6) + 6;
            if (gap < needed) return null;
            // …and the same against the start-anchored first label.
            const gapFirst = toX(index, categories.length) - toX(0, categories.length);
            const neededFirst = (categories[0]!.length * 6) + (label.length * 6) / 2 + 6;
            if (gapFirst < neededFirst) return null;
          }
          return (
            <text
              key={`${index}-${label}`}
              x={toX(index, categories.length)}
              y={tickRowY}
              textAnchor={index === 0 ? 'start' : index === categories.length - 1 ? 'end' : 'middle'}
              data-trend-x-tick
              className="fill-[var(--ink-tertiary)] text-[10px]"
            >
              {label}
            </text>
          );
        })}
        {xAxisLabel && !compact ? (
          <text
            x={pad.left + (width - pad.left - pad.right) / 2}
            y={height - 3}
            textAnchor="middle"
            className="fill-[var(--ink-tertiary)] text-[10px]"
          >
            {xAxisLabel}
          </text>
        ) : null}
      </svg>
      {legendSeries.length > 1 ? (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {legendSeries.map((row) => (
            <span key={row.name} className="inline-flex items-center gap-1.5 text-xs text-muted">
              {strokeFor(row).shaded ? (
                <svg width={16} height={8} viewBox="0 0 16 8" aria-hidden className="shrink-0">
                  <line
                    x1={2}
                    x2={14}
                    y1={4}
                    y2={4}
                    stroke={strokeFor(row).stroke}
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeDasharray={SHADED_DASH}
                  />
                </svg>
              ) : (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: strokeFor(row).stroke }}
                />
              )}
              {row.name}
            </span>
          ))}
          {legendNote ? <span className="text-xs text-muted" data-legend-note>{legendNote}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
